import { useMemo, useState } from 'react';
import { useSession } from '../session/context';
import { useLicense } from '../license/context';
import { UpgradePrompt } from '../license/UpgradePrompt';
import { friendlyErrorMessage } from '../session/session';
import {
  exportAndDownload,
  isProTarget,
  targetName,
  type TargetId,
} from '../session/exportDownload';
import { FormatPicker } from '../convert/FormatPicker';
import { LossSummary, WarningList } from '../convert/LossMatrix';
import { capabilityReport, type ParseWarning } from '../../core';
import { track } from '../analytics/client';
import './StudioExport.css';

/**
 * In-place export from Studio (cue-forge-6f7): the document is shared across
 * modes, so an editor working against the video shouldn't have to switch to
 * Convert to get their file out. Mirrors the Converter's target picker + export
 * path (exportAndDownload) — any format, Pro codecs gated inline the same way —
 * and defaults to the loaded source format so "Export" re-saves in place.
 */
export function StudioExport() {
  const { loaded, markClean } = useSession();
  const { isPro } = useLicense();
  const [target, setTarget] = useState<TargetId>(() => loaded?.sourceFormat ?? 'srt');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [warnings, setWarnings] = useState<ParseWarning[]>([]);

  // Same source of truth the Converter's LossMatrix reads (core capabilityReport),
  // so the Studio picker shows the identical proactive Kept/Lost verdict — an
  // in-place export is no longer less honest than a Convert (cue-forge-zxw.5).
  const report = useMemo(() => (loaded ? capabilityReport(loaded.doc) : []), [loaded]);

  if (!loaded) return null;

  const locked = isProTarget(target) && !isPro;
  // A 0-cue document would export an empty deliverable — block it with an
  // explanation instead of succeeding silently (cue-forge-w28.16, drt.15).
  const noCues = loaded.doc.cues.length === 0;
  const current = report.find((e) => e.target === target);
  // The doc only warrants a loss line when it actually uses styling/positioning.
  const usesStyling = !!current && current.preserved.length + current.dropped.length > 0;

  async function onExport() {
    if (!loaded || locked || noCues) return;
    setError(null);
    try {
      const { warnings: w } = await exportAndDownload(loaded, target);
      setWarnings(w);
      markClean();
      setStatus(`Exported ${targetName(target)}`);
      track('export', { format: target, surface: 'studio' });
    } catch (err) {
      setStatus('');
      setWarnings([]);
      // Expected errors (e.g. charset preflight) carry user-facing copy; anything
      // else collapses to a calm sentence instead of a raw stack string (w28.12).
      setError(
        friendlyErrorMessage(
          err,
          `Couldn't export to ${targetName(target)}. Try a different target format, or re-open the source file and try again.`,
        ),
      );
    }
  }

  return (
    <div className="cf-studio-export">
      <FormatPicker
        label="Export as"
        ariaLabel="Export format"
        value={target}
        onChange={setTarget}
        isPro={isPro}
      />
      <button
        type="button"
        className="cf-btn"
        onClick={() => void onExport()}
        disabled={locked || noCues}
      >
        Export
        {locked && (
          <span className="cf-lock" aria-hidden="true">
            {' \u{1F512}'}
          </span>
        )}
      </button>
      {/* Disabled-with-reason (drt.15): say WHY Export is off for a 0-cue doc. */}
      {noCues && (
        <span className="cf-studio-export__note">
          Nothing to export — the document has no cues.
        </span>
      )}
      {/* Proactive current-target loss preview: the compact half of the Converter's
          LossMatrix, off the same capabilityReport data + shared CSS classes, so the
          in-place export shows what the picked format keeps/drops before you click. */}
      {usesStyling && current && (
        <span className="cf-studio-export__loss" aria-live="polite">
          <LossSummary preserved={current.preserved} dropped={current.dropped} />
        </span>
      )}
      {/* Always-mounted polite live region for the export confirmation. */}
      <span className="cf-studio-export__status" role="status" aria-live="polite">
        {status}
      </span>
      {locked && <UpgradePrompt feature={`${targetName(target)} export`} surface="export" />}
      {error && (
        <p className="cf-studio-export__error" role="alert">
          {error}
        </p>
      )}
      {/* Post-export conversion warnings via the shared WarningList (cue-forge-zxw.5
          honesty, now the same component the Converter uses — cue-forge-zxw.10). */}
      <WarningList title="Conversion" warnings={warnings.map((w) => w.message)} />
    </div>
  );
}
